// Charts in lanes, as the prototype draws them ("Ein Bezug", "Bezug läuft", canvas 21.10.2026): every measure in a
// lane of its own on one time axis, a short line and its name with the unit at the lane's top left, words at a
// curve's end or at its highest or lowest point instead of scales, the time below; a cursor that follows the finger
// reads every lane at one moment. The same look for a shot, the running shot, the last 20 minutes and the bars of the
// last shots (Dominik, 21.10.2026: the temperature history "nicht aligned an unsere neue Graphen Anzeige").
// Only data and words here (pure); ui/components/LaneChart.tsx draws them in pixels.
import type { ShotItem } from '@brewcore/app-contract';
import type { HistorySample } from '../history.js';
import type { CurveData } from './curves.js';
import type { ScreenContext } from './context.js';
import {
  deviation,
  formatAuto,
  formatNumber,
  metaOf,
  offTarget,
  shotMs,
  shotTime,
  whenText,
} from './format.js';

export type LaneTone = 'text' | 'cool' | 'heat' | 'dim' | 'accent';

/** x (seconds of a shot, ms of the clock, the slot of a shot) and the value there; null breaks the line */
export type LanePoint = readonly [x: number, v: number | null];

/** Words next to a curve: strong ones in Poppins and the curve's colour, the others small and dim. */
export interface LaneMark {
  at: 'end' | 'max' | 'min';
  text: string;
  strong: boolean;
}

export interface LaneSeries {
  key: string;
  points: readonly LanePoint[];
  tone: LaneTone;
  /** stroke width in px */
  width: number;
  dashed?: boolean;
  /** finer than dashed (the third shot of a comparison) */
  dotted?: boolean;
  /** a soft fill under it (the weight of the running shot) */
  area?: boolean;
  /** another shot behind this one: thin, dashed and faint, no words, no dot */
  faint?: boolean;
  /** the lane's value range for this series */
  lo: number;
  hi: number;
  marks?: LaneMark[];
  /** a dot at the last point (the running shot now) */
  dot?: boolean;
}

/** A level across the lane (the target weight), its words above the line or right of its end. */
export interface LaneRule {
  v: number;
  lo: number;
  hi: number;
  label: string;
  place: 'above' | 'end';
}

/** A soft band between two lines (the usual range of the last shots), its name above it at `labelAt`. */
export interface LaneBand {
  low: readonly LanePoint[];
  high: readonly LanePoint[];
  lo: number;
  hi: number;
  label: string;
  labelAt: number;
}

/** Columns instead of lines (the last shots): one per slot, off-target ones in the danger colour, the newest strong. */
export interface LaneBars {
  items: { x: number; v: number; tone: 'normal' | 'off' | 'last' }[];
  lo: number;
  hi: number;
  /** the newest value at the right end */
  mark: string | null;
}

export interface Lane {
  key: string;
  /** height of the plot in px (the legend sits above it) */
  height: number;
  /** `plain`: the lane's name without a line in front (a comparison names its shots in the first lane) */
  legend: { label: string; tone: LaneTone; dashed?: boolean; dotted?: boolean; plain?: boolean }[];
  series: LaneSeries[];
  rules: LaneRule[];
  band: LaneBand | null;
  /** vertical lines (pump off), their name in the legend strip, ending at the line */
  marks: { x: number; label: string }[];
  bars: LaneBars | null;
}

export interface ScrubReading {
  x: number;
  title: string;
  values: string;
  /** the dot of every series at the cursor */
  dots: { lane: number; series: string; v: number }[];
}

export interface LaneChartView {
  x: { from: number; to: number; ticks: { v: number; label: string }[] };
  lanes: Lane[];
  /** a band behind all lanes (the pre-infusion until the first drop), its name at the first lane's bottom */
  span: { from: number; to: number; label: string } | null;
  /** room right of the plot for the words at the curves' ends (px) */
  gutter: number;
  /** what the chart shows, for screen readers */
  label: string;
  /** the header without the cursor ("Ganzer Bezug", "29 s · 37,0 g · Ø 1,8 g/s"); null: the chart has no header */
  whole: { title: string; values: string } | null;
  /** the header and the dots at one x; null: the chart cannot be read with a cursor */
  at: ((x: number) => ScrubReading | null) | null;
  /** how far one arrow key moves the cursor */
  step: number;
}

// ------------------------------------------------------------------------------------------------ helpers

/** The value of a line at x: between its two neighbours, null outside it or in a gap. */
export function valueAt(points: readonly LanePoint[], x: number): number | null {
  const first = points[0];
  if (!first || x < first[0]) return null;
  if (x === first[0]) return first[1];
  for (let i = 1; i < points.length; i++) {
    const p = points[i] as LanePoint;
    if (p[0] < x) continue;
    if (p[0] === x) return p[1];
    const q = points[i - 1] as LanePoint;
    if (p[1] === null || q[1] === null) return null;
    return q[1] + ((p[1] - q[1]) * (x - q[0])) / (p[0] - q[0]);
  }
  return null;
}

/** A line cut to [from, to]: the points inside and, where it runs past an edge, its value at the edge. */
export function clipPoints(points: readonly LanePoint[], from: number, to: number): LanePoint[] {
  const out: LanePoint[] = [];
  const at = (a: LanePoint, b: LanePoint, x: number): LanePoint => [
    x,
    a[1] === null || b[1] === null ? null : a[1] + ((b[1] - a[1]) * (x - a[0])) / (b[0] - a[0]),
  ];
  points.forEach((p, i) => {
    const prev = points[i - 1];
    if (prev && prev[0] < from && p[0] > from) out.push(at(prev, p, from));
    if (p[0] >= from && p[0] <= to) out.push(p);
    if (prev && prev[0] < to && p[0] > to) out.push(at(prev, p, to));
  });
  return out;
}

/**
 * The chart cut to its time axis: no curve, band, mark or column is drawn left or right of the plot (Dominik,
 * 23.10.2026: the usual range of slower shots ran past the right edge of the card). LaneChart draws only this.
 */
export function fitLanes(view: LaneChartView): LaneChartView {
  const { from, to } = view.x;
  const cut = (points: readonly LanePoint[]) => clipPoints(points, from, to);
  const inside = (x: number) => x >= from && x <= to;
  return {
    ...view,
    lanes: view.lanes.map((lane) => {
      const band = lane.band && { ...lane.band, low: cut(lane.band.low), high: cut(lane.band.high) };
      return {
        ...lane,
        series: lane.series.map((s) => ({ ...s, points: cut(s.points) })),
        band: band && band.high.length > 1 ? { ...band, labelAt: Math.min(to, band.labelAt) } : null,
        marks: lane.marks.filter((m) => inside(m.x)),
        bars: lane.bars && { ...lane.bars, items: lane.bars.items.filter((b) => inside(b.x)) },
      };
    }),
    span:
      view.span && view.span.to > from && view.span.from < to
        ? { ...view.span, from: Math.max(from, view.span.from), to: Math.min(to, view.span.to) }
        : null,
  };
}

/**
 * A line for the eye: each value becomes the mean of its neighbours up to `half` (x units) away on both sides, within
 * its own run (a gap stays a gap). Near the ends of a run the window shrinks, so the first and the last value stay as
 * measured. The sensor counts in 0.1 K steps and sometimes jumps once; drawn as measured, a curve over a few kelvin
 * looks like stairs (Dominik, 21.10.2026: "sehr zerstückelt"). Readouts keep the measured values.
 */
export function smoothPoints(points: readonly LanePoint[], half: number): LanePoint[] {
  const out: LanePoint[] = [];
  let start = 0;
  while (start < points.length) {
    const p = points[start] as LanePoint;
    if (p[1] === null) {
      out.push(p);
      start++;
      continue;
    }
    let end = start;
    while (end < points.length && (points[end] as LanePoint)[1] !== null) end++;
    const x0 = p[0];
    const x1 = (points[end - 1] as LanePoint)[0];
    for (let i = start; i < end; i++) {
      const [x, v] = points[i] as readonly [number, number];
      const h = Math.min(half, x - x0, x1 - x);
      let sum = v;
      let n = 1;
      for (let j = i - 1; j >= start && x - (points[j] as LanePoint)[0] <= h; j--, n++)
        sum += (points[j] as LanePoint)[1] as number;
      for (let j = i + 1; j < end && (points[j] as LanePoint)[0] - x <= h; j++, n++)
        sum += (points[j] as LanePoint)[1] as number;
      out.push([x, sum / n]);
    }
    start = end;
  }
  return out;
}

const finite = (pts: readonly LanePoint[]): number[] =>
  pts.map((p) => p[1]).filter((v): v is number => v !== null && Number.isFinite(v));

/** A channel of a shot as points on its time axis (s). */
export const channelPoints = (d: CurveData, values: readonly (number | null)[] | null): LanePoint[] =>
  (values ?? []).map((v, k) => [(k * d.dt) / 1000, v !== null && Number.isFinite(v) ? v : null] as const);

const lengthS = (d: CurveData): number =>
  ((Math.max(d.weight?.length ?? 0, d.flow?.length ?? 0, d.temp?.length ?? 0) - 1) * d.dt) / 1000;

/** The next multiple of `step` at or above v. */
const ceilTo = (v: number, step: number) => Math.ceil(v / step - 1e-9) * step;

/** Time ticks of a shot: every 5, 10 or 20 s, the unit at the first and the last ("0 s", "10", "20", "30 s"). */
export function secondTicks(to: number): { v: number; label: string }[] {
  const step = to > 64 ? 20 : to > 18 ? 10 : 5;
  const out: { v: number; label: string }[] = [];
  for (let s = 0; s <= to + 1e-9; s += step) out.push({ v: s, label: String(s) });
  if (out.length) {
    (out[0] as { label: string }).label = '0 s';
    const last = out[out.length - 1] as { v: number; label: string };
    if (out.length > 1) last.label = `${last.v} s`;
  }
  return out;
}

/**
 * Range of a temperature lane: around its values, at least `min` K; more room below than above, where the lowest
 * point's value is written under the curve.
 */
function tempRange(values: number[], min = 3): { lo: number; hi: number } {
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const pad = Math.max(0, min - (hi - lo));
  return { lo: lo - (hi - lo) * 0.6 - 0.3 - pad * 0.6, hi: hi + (hi - lo) * 0.15 + 0.1 + pad * 0.4 };
}

/** Average flow while coffee ran (samples above 0.3 g/s), null without flow. */
export function averageFlow(d: CurveData): number | null {
  const f = (d.flow ?? []).filter((v): v is number => v !== null && v > 0.3);
  return f.length ? f.reduce((a, b) => a + b, 0) / f.length : null;
}

/**
 * The usual range of the weight (prototype "üblich"): at every half second the lowest and the highest weight of the
 * other shots; a shot that ended keeps its last weight. Null with fewer than two shots with a weight.
 */
export function usualBand(curves: readonly CurveData[]): { low: LanePoint[]; high: LanePoint[] } | null {
  const lines = curves
    .map((d) => channelPoints(d, d.weight).filter((p) => p[1] !== null))
    .filter((l) => l.length > 1);
  if (lines.length < 2) return null;
  const end = Math.min(...lines.map((l) => (l[l.length - 1] as LanePoint)[0]));
  const low: LanePoint[] = [];
  const high: LanePoint[] = [];
  for (let x = 0; x <= end + 1e-9; x += 0.5) {
    const vs = lines.map((l) => valueAt(l, x) ?? 0);
    // the band starts once the first of them has coffee in the cup (before, it is a line on the floor)
    if (!high.length && Math.max(...vs) < 0.5) continue;
    low.push([x, Math.min(...vs)]);
    high.push([x, Math.max(...vs)]);
  }
  return high.length > 1 ? { low, high } : null;
}

// ------------------------------------------------------------------------------------------------ one shot

export interface ShotLaneOptions {
  /** the first drop (s), the pre-infusion band ends there; without it the configured pre-infusion */
  firstDrop?: number | null;
  /** seconds of pre-infusion configured (burst and pause), 0 without */
  piSeconds?: number;
  targetWeight?: number | null;
  /** the usual range of the last shots of this bean */
  usual?: { low: readonly LanePoint[]; high: readonly LanePoint[] } | null;
  /** another shot behind it (the comparison page) */
  other?: CurveData | null;
  /** the time of the shot without its pre-infusion, as its figures show it ("29 s") */
  timeText?: string;
}

/**
 * A stored shot in three lanes (prototype "Ein Bezug"): weight with the target, the usual range, where the pump stopped
 * and the weight at the end; flow with its most; the brew temperature dashed, its end and its lowest point. A channel
 * the shot does not have is no lane.
 */
export function shotLanes(ctx: ScreenContext, d: CurveData, o: ShotLaneOptions = {}): LaneChartView {
  const n = (v: number, digits: number) => formatNumber(v, digits, ctx.lang);
  const len = Math.max(1, lengthS(d), o.other ? lengthS(o.other) : 0);
  const to = len + Math.max(2, len * 0.1);
  const weight = d.weight ? channelPoints(d, d.weight) : null;
  const flow = d.flow ? channelPoints(d, d.flow) : null;
  const temp = d.temp ? channelPoints(d, d.temp) : null;
  const ow = o.other?.weight ? channelPoints(o.other, o.other.weight) : null;
  const of = o.other?.flow ? channelPoints(o.other, o.other.flow) : null;
  const ot = o.other?.temp ? channelPoints(o.other, o.other.temp) : null;
  const stopS = d.stop >= 0 ? (d.stop * d.dt) / 1000 : null;
  const target = o.targetWeight && o.targetWeight > 0 && weight ? o.targetWeight : null;
  const lanes: Lane[] = [];
  if (weight) {
    const all = [...finite(weight), ...(ow ? finite(ow) : []), ...(o.usual ? finite(o.usual.high) : [])];
    const hi = ceilTo(Math.max(10, target ?? 0, ...all) * 1.04, 5);
    const end = finite(weight).at(-1);
    // the usual range while this shot ran: it ends with the shot, not under the words at its end
    const own = lengthS(d);
    const band = o.usual
      ? (() => {
          const at = Math.min(len * 0.45, (o.usual.high.at(-1)?.[0] ?? 0) * 0.9);
          const low = clipPoints(o.usual.low, 0, own);
          const high = clipPoints(o.usual.high, 0, own);
          return high.length > 1 ? { low, high, lo: 0, hi, label: ctx.tr('usualBand'), labelAt: at } : null;
        })()
      : null;
    lanes.push({
      key: 'weight',
      height: 136,
      legend: [{ label: ctx.tr('legendWeight'), tone: 'text' }],
      series: [
        ...(ow
          ? [{ key: 'other-weight', points: ow, tone: 'dim' as const, width: 1.5, faint: true, lo: 0, hi }]
          : []),
        {
          key: 'weight',
          points: weight,
          tone: 'text',
          width: 2.5,
          lo: 0,
          hi,
          marks: end !== undefined ? [{ at: 'end', text: `${Math.round(end)} g`, strong: true }] : [],
        },
      ],
      rules: target
        ? [
            {
              v: target,
              lo: 0,
              hi,
              label: `${ctx.tr('goal')} ${formatAuto(target, ctx.lang)} g`,
              place: 'above',
            },
          ]
        : [],
      band,
      marks: stopS !== null && stopS < len ? [{ x: stopS, label: ctx.tr('pumpOff') }] : [],
      bars: null,
    });
  }
  if (flow && finite(flow).length) {
    const most = Math.max(...finite(flow), ...(of ? finite(of) : []));
    const hi = Math.max(1, ceilTo(most * 1.25, 0.2));
    lanes.push({
      key: 'flow',
      height: 52,
      legend: [{ label: ctx.tr('legendFlow'), tone: 'cool' }],
      series: [
        ...(of
          ? [{ key: 'other-flow', points: of, tone: 'dim' as const, width: 1.5, faint: true, lo: 0, hi }]
          : []),
        {
          key: 'flow',
          points: flow,
          tone: 'cool',
          width: 2,
          lo: 0,
          hi,
          marks: [{ at: 'max', text: `${n(Math.max(...finite(flow)), 1)} g/s`, strong: true }],
        },
      ],
      rules: [],
      band: null,
      marks: [],
      bars: null,
    });
  }
  if (temp && finite(temp).length) {
    const vs = finite(temp);
    const { lo, hi } = tempRange([...vs, ...(ot ? finite(ot) : [])]);
    const last = vs.at(-1) as number;
    const min = Math.min(...vs);
    lanes.push({
      key: 'temp',
      height: 44,
      legend: [{ label: ctx.tr('legendTemp'), tone: 'heat', dashed: true }],
      series: [
        ...(ot
          ? [{ key: 'other-temp', points: ot, tone: 'dim' as const, width: 1.5, faint: true, lo, hi }]
          : []),
        {
          key: 'temp',
          points: smoothPoints(temp, 0.5),
          tone: 'heat',
          width: 2,
          dashed: true,
          lo,
          hi,
          marks: [
            { at: 'end', text: `${n(last, 1)} °C`, strong: true },
            ...(last - min >= 0.3 ? [{ at: 'min' as const, text: `${n(min, 1)} °C`, strong: false }] : []),
          ],
        },
      ],
      rules: [],
      band: null,
      marks: [],
      bars: null,
    });
  }
  const drop = typeof o.firstDrop === 'number' && o.firstDrop > 0 ? o.firstDrop : null;
  const piEnd = o.piSeconds && o.piSeconds > 0 ? (drop ?? o.piSeconds) : null;
  const final = weight ? finite(weight).at(-1) : undefined;
  const avg = averageFlow(d);
  const wholeValues = [
    o.timeText ?? `${n(len, 0)} s`,
    final !== undefined ? `${n(final, 1)} g` : '',
    avg !== null ? `${ctx.tr('avgShort')} ${n(avg, 1)} g/s` : '',
  ].filter(Boolean);
  const series = lanes.flatMap((l, lane) => l.series.filter((s) => !s.faint).map((s) => ({ lane, s })));
  return {
    x: { from: 0, to, ticks: secondTicks(to) },
    lanes,
    span: piEnd !== null ? { from: 0, to: Math.min(piEnd, len), label: ctx.tr('sPreinf') } : null,
    gutter: 34,
    label: ctx.tr('shotChartLabel', { v: wholeValues.join(', ') }),
    whole: { title: ctx.tr('wholeShot'), values: wholeValues.join(' · ') },
    at: (x) => {
      const t = Math.max(0, Math.min(len, x));
      const dots = series
        .map(({ lane, s }) => ({ lane, series: s.key, v: valueAt(s.points, t) }))
        .filter((p): p is { lane: number; series: string; v: number } => p.v !== null);
      const text = dots.map((p) =>
        p.series === 'weight'
          ? `${n(p.v, 1)} g`
          : p.series === 'flow'
            ? `${n(p.v, 1)} g/s`
            : `${n(p.v, 1)} °C`,
      );
      return { x: t, title: ctx.tr('atSeconds', { s: n(t, 1) }), values: text.join(' · '), dots };
    },
    step: 0.5,
  };
}

// ------------------------------------------------------------------------------------------------ the running shot

/**
 * The running shot (prototype "Bezug läuft"): weight with a soft fill and flow in one lane on their own scales, the
 * target as a dashed line with its weight at the right, a dot and the value at the newest point of each.
 */
export function liveLanes(
  ctx: ScreenContext,
  d: CurveData,
  o: { targetWeight: number | null; spanS: number },
): LaneChartView {
  const n = (v: number, digits: number) => formatNumber(v, digits, ctx.lang);
  const len = Math.max(1, lengthS(d));
  const to = Math.max(o.spanS + 2, len * 1.1);
  const weight = d.weight ? channelPoints(d, d.weight) : null;
  const flow = d.flow ? channelPoints(d, d.flow) : null;
  const target = o.targetWeight && o.targetWeight > 0 && weight ? o.targetWeight : null;
  const wMax = weight ? Math.max(10, target ?? 0, ...finite(weight)) : 0;
  const wHi = ceilTo(wMax * 1.1, 5);
  const fHi = flow ? Math.max(3, ceilTo(Math.max(0, ...finite(flow)) * 1.2, 0.5)) : 3;
  const lastW = weight ? finite(weight).at(-1) : undefined;
  const lastF = flow ? finite(flow).at(-1) : undefined;
  const series: LaneSeries[] = [];
  if (weight)
    series.push({
      key: 'weight',
      points: weight,
      tone: 'text',
      width: 2.5,
      area: true,
      dot: true,
      lo: 0,
      hi: wHi,
      marks: lastW !== undefined ? [{ at: 'end', text: `${n(lastW, 1)} g`, strong: true }] : [],
    });
  if (flow)
    series.push({
      key: 'flow',
      points: flow,
      tone: 'cool',
      width: 2,
      dot: true,
      lo: 0,
      hi: fHi,
      marks: lastF !== undefined ? [{ at: 'end', text: `${n(lastF, 1)} g/s`, strong: true }] : [],
    });
  return {
    x: { from: 0, to, ticks: secondTicks(to) },
    lanes: [
      {
        key: 'live',
        height: 160,
        legend: [
          ...(weight ? [{ label: ctx.tr('legendWeight'), tone: 'text' as const }] : []),
          ...(flow ? [{ label: ctx.tr('legendFlow'), tone: 'cool' as const }] : []),
        ],
        series,
        rules: target
          ? [{ v: target, lo: 0, hi: wHi, label: `${formatAuto(target, ctx.lang)} g`, place: 'end' }]
          : [],
        band: null,
        marks: [],
        bars: null,
      },
    ],
    span: null,
    gutter: 34,
    label: ctx.tr('liveChartLabel', {
      s: n(len, 0),
      v: [lastW !== undefined ? `${n(lastW, 1)} g` : '', lastF !== undefined ? `${n(lastF, 1)} g/s` : '']
        .filter(Boolean)
        .join(', '),
    }),
    whole: null,
    at: null,
    step: 1,
  };
}

// ------------------------------------------------------------------------------------------------ the last 20 minutes

/**
 * The last 20 minutes in the same look: the temperature with its setpoint dashed behind it, the heater's power in a
 * small lane below, the newest value at the right, minutes on the axis; the cursor reads any moment.
 */
export function historyLanes(
  ctx: ScreenContext,
  samples: readonly HistorySample[],
  from: number,
  to: number,
): LaneChartView {
  const n = (v: number, digits: number) => formatNumber(v, digits, ctx.lang);
  const temp: LanePoint[] = samples.map((s) => [s.t, s.temp !== null && s.temp > 0 ? s.temp : null] as const);
  const target: LanePoint[] = samples.map(
    (s) => [s.t, s.target !== null && s.target > 0 ? s.target : null] as const,
  );
  const power: LanePoint[] = samples.map((s) => [s.t, s.power] as const);
  const vs = [...finite(temp), ...finite(target)];
  // a little room above and below (no words under this curve), at least 4 K
  const range = (xs: number[]) => {
    const a = Math.min(...xs);
    const b = Math.max(...xs);
    const pad = Math.max(1, (b - a) * 0.08, (4 - (b - a)) / 2);
    return { lo: a - pad, hi: b + pad };
  };
  const { lo, hi } = vs.length ? range(vs) : { lo: 0, hi: 100 };
  const lastT = finite(temp).at(-1);
  const lastP = finite(power).at(-1);
  const hasPower = finite(power).length > 0;
  const mins = (t: number) => Math.round((to - t) / 60_000);
  const values = (t: number | undefined, p: number | undefined) =>
    [
      t !== undefined ? `${n(t, 1)} °C` : '',
      p !== undefined ? `${ctx.tr('heatLine')} ${Math.round(p)} %` : '',
    ].filter(Boolean);
  const lanes: Lane[] = [
    {
      key: 'temp',
      height: 96,
      legend: [
        { label: ctx.tr('legendTemp'), tone: 'heat' },
        { label: ctx.tr('setLine'), tone: 'dim', dashed: true },
      ],
      series: [
        { key: 'target', points: target, tone: 'dim', width: 1.5, dashed: true, lo, hi },
        {
          key: 'temp',
          points: smoothPoints(temp, 8_000),
          tone: 'heat',
          width: 2,
          lo,
          hi,
          marks: lastT !== undefined ? [{ at: 'end', text: `${n(lastT, 1)} °C`, strong: true }] : [],
        },
      ],
      rules: [],
      band: null,
      marks: [],
      bars: null,
    },
  ];
  if (hasPower)
    lanes.push({
      key: 'power',
      height: 36,
      legend: [{ label: ctx.tr('legendPower'), tone: 'cool' }],
      series: [
        {
          key: 'power',
          points: power,
          tone: 'cool',
          width: 1.5,
          lo: 0,
          hi: 100,
          marks: lastP !== undefined ? [{ at: 'end', text: `${Math.round(lastP)} %`, strong: true }] : [],
        },
      ],
      rules: [],
      band: null,
      marks: [],
      bars: null,
    });
  const series = lanes.flatMap((l, lane) => l.series.map((s) => ({ lane, s })));
  return {
    x: {
      from,
      to,
      ticks: [
        { v: from, label: ctx.tr('minutesAgo', { n: mins(from) }) },
        { v: (from + to) / 2, label: ctx.tr('minutesAgo', { n: mins((from + to) / 2) }) },
        { v: to, label: ctx.tr('nowShort') },
      ],
    },
    lanes,
    span: null,
    gutter: 58,
    label: ctx.tr('historyChartLabel', { v: values(lastT, lastP).join(', ') || '–' }),
    whole: { title: ctx.tr('history'), values: values(lastT, lastP).join(' · ') },
    at: (x) => {
      const t = Math.max(from, Math.min(to, x));
      const dots = series
        .map(({ lane, s }) => ({ lane, series: s.key, v: valueAt(s.points, t) }))
        .filter((p): p is { lane: number; series: string; v: number } => p.v !== null);
      const get = (key: string) => dots.find((p) => p.series === key)?.v;
      const ago = Math.round((to - t) / 1000);
      return {
        x: t,
        title:
          ago < 30
            ? ctx.tr('nowShort')
            : ago < 90
              ? ctx.tr('secondsAgo', { n: ago })
              : ctx.tr('minutesAgo', { n: Math.round(ago / 60) }),
        values: values(valueAt(temp, t) ?? undefined, get('power')).join(' · '),
        dots: dots.filter((p) => p.series !== 'target'),
      };
    },
    step: 30_000,
  };
}

// ------------------------------------------------------------------------------------------------ the last shots

/** At most 12 shots; at least 6 slots, so a few shots do not stand as wide blocks. */
export const BAR_SLOTS = 12;
const BAR_MIN_SLOTS = 6;

export interface ShotBarsView {
  /** "Die letzten 12" */
  title: string;
  chart: LaneChartView;
  /** the shot behind every slot, oldest first, for the tap that opens it */
  shots: { id: string; slot: number; label: string }[];
}

/**
 * The last twelve shots as columns (Dominik, 21.10.2026: the bars stay, but better): time and weight each in a lane of
 * its own with the unit, the target as a dashed line, shots that missed it in the danger colour, the newest strong with
 * its value at the right. A tap on a column opens the shot. Null below three shots.
 */
export function shotBarsView(ctx: ScreenContext, items: readonly ShotItem[]): ShotBarsView | null {
  const shots = items.slice(0, BAR_SLOTS).reverse();
  if (shots.length < 3) return null;
  const slots = Math.max(BAR_MIN_SLOTS, shots.length);
  const start = slots - shots.length;
  const n = (v: number, digits: number) => formatNumber(v, digits, ctx.lang);
  const rows = shots.map((s, i) => {
    const m = metaOf(s);
    const dev = deviation(m);
    return {
      s,
      slot: start + i,
      time: shotTime(m).main,
      weight: typeof m.weight === 'number' ? m.weight : null,
      off: offTarget(dev),
      unit: dev?.unit ?? null,
      target:
        m.stopMode === 'weight'
          ? (m.targetWeight ?? null)
          : m.stopMode === 'time'
            ? (m.targetTime ?? null)
            : null,
    };
  });
  const last = rows.length - 1;
  const tone = (i: number, off: boolean) =>
    (i === last ? 'last' : off ? 'off' : 'normal') as 'normal' | 'off' | 'last';
  const tMax = ceilTo(Math.max(30, ...rows.map((r) => r.time)) * 1.08, 5);
  const weights = rows.filter((r) => r.weight !== null);
  const wMax = ceilTo(Math.max(40, ...weights.map((r) => r.weight as number)) * 1.08, 5);
  const newest = rows[last];
  const tTarget = rows.filter((r) => r.unit === 's' && r.target).at(-1)?.target ?? null;
  const wTarget = rows.filter((r) => r.unit === 'g' && r.target).at(-1)?.target ?? null;
  const lanes: Lane[] = [
    {
      key: 'time',
      height: 44,
      legend: [{ label: ctx.tr('legendTime'), tone: 'text' }],
      series: [],
      rules: tTarget ? [{ v: tTarget, lo: 0, hi: tMax, label: '', place: 'above' }] : [],
      band: null,
      marks: [],
      bars: {
        items: rows.map((r, i) => ({ x: r.slot, v: r.time, tone: tone(i, r.off && r.unit === 's') })),
        lo: 0,
        hi: tMax,
        mark: newest ? `${n(newest.time, 0)} s` : null,
      },
    },
  ];
  if (weights.length)
    lanes.push({
      key: 'weight',
      height: 44,
      legend: [{ label: ctx.tr('legendWeight'), tone: 'text' }],
      series: [],
      rules: wTarget ? [{ v: wTarget, lo: 0, hi: wMax, label: '', place: 'above' }] : [],
      band: null,
      marks: [],
      bars: {
        items: rows
          .map((r, i) =>
            r.weight !== null ? { x: r.slot, v: r.weight, tone: tone(i, r.off && r.unit === 'g') } : null,
          )
          .filter((b): b is { x: number; v: number; tone: 'normal' | 'off' | 'last' } => b !== null),
        lo: 0,
        hi: wMax,
        mark: newest?.weight !== null && newest?.weight !== undefined ? `${n(newest.weight, 1)} g` : null,
      },
    });
  const offN = rows.filter((r) => r.off).length;
  const read = (r: (typeof rows)[number]) =>
    [`${n(r.time, 0)} s`, r.weight !== null ? `${n(r.weight, 1)} g` : ''].filter(Boolean).join(' · ');
  return {
    title: ctx.tr('barsTitle', { n: rows.length }),
    chart: {
      x: { from: -0.5, to: slots - 0.5, ticks: [] },
      lanes,
      span: null,
      gutter: 46,
      label: ctx.tr('barsLabel', { n: rows.length, off: offN }),
      // a tap on a column (or the arrow keys) shows its shot in the header; the list above opens it
      whole: { title: ctx.tr('barsTitle', { n: rows.length }), values: '' },
      at: (x) => {
        const slot = Math.round(Math.max(start, Math.min(slots - 1, x)));
        const r = rows.find((q) => q.slot === slot);
        if (!r) return null;
        return { x: slot, title: whenText(shotMs(r.s), ctx.lang, ctx.now), values: read(r), dots: [] };
      },
      step: 1,
    },
    shots: rows.map((r) => ({
      id: r.s.id,
      slot: r.slot,
      label: [
        whenText(shotMs(r.s), ctx.lang, ctx.now),
        `${n(r.time, 0)} s`,
        r.weight !== null ? `${n(r.weight, 1)} g` : '',
      ]
        .filter(Boolean)
        .join(', '),
    })),
  };
}

// ------------------------------------------------------------------------------------------------ comparison

const CMP = [
  { tone: 'text' as const, width: 2.5 },
  { tone: 'cool' as const, width: 2, dashed: true },
  { tone: 'dim' as const, width: 2, dotted: true },
];

/**
 * Two or three shots over each other (page "Vergleich") in the same lanes: weight, flow, temperature on one time
 * axis, the first shot solid, the second dashed, the third dotted (the page names them above the chart); the weight
 * at each end. A channel no shot has is no lane; a shot still loading is left out until it came.
 */
export function compareLanes(
  ctx: ScreenContext,
  shots: readonly { label: string; curve: CurveData | null | undefined }[],
): LaneChartView {
  const n = (v: number, digits: number) => formatNumber(v, digits, ctx.lang);
  const loaded = shots
    .map((x, i) => ({ ...x, i }))
    .filter((x): x is { label: string; curve: CurveData; i: number } => !!x.curve);
  const len = Math.max(1, ...loaded.map((x) => lengthS(x.curve)));
  const to = len + Math.max(2, len * 0.1);
  const lanes: Lane[] = [];
  const channels: { key: 'weight' | 'flow' | 'temp'; label: string; height: number; unit: string }[] = [
    { key: 'weight', label: ctx.tr('legendWeight'), height: 120, unit: 'g' },
    { key: 'flow', label: ctx.tr('legendFlow'), height: 52, unit: 'g/s' },
    { key: 'temp', label: ctx.tr('legendTemp'), height: 52, unit: '°C' },
  ];
  for (const c of channels) {
    const lines = loaded
      .map((x) => ({ x, points: x.curve[c.key] ? channelPoints(x.curve, x.curve[c.key]) : null }))
      .filter(
        (l): l is { x: (typeof loaded)[number]; points: LanePoint[] } =>
          !!l.points && finite(l.points).length > 0,
      );
    if (!lines.length) continue;
    const vs = lines.flatMap((l) => finite(l.points));
    const range =
      c.key === 'temp'
        ? tempRange(vs)
        : {
            lo: 0,
            hi:
              c.key === 'weight'
                ? ceilTo(Math.max(10, ...vs) * 1.04, 5)
                : Math.max(1, ceilTo(Math.max(...vs) * 1.25, 0.2)),
          };
    lanes.push({
      key: c.key,
      height: c.height,
      legend: [{ label: c.label, tone: 'dim', plain: true }],
      series: lines.map(({ x, points }) => {
        const style = CMP[x.i] ?? { tone: 'dim' as const, width: 2 };
        const end = finite(points).at(-1);
        return {
          key: `${c.key}-${x.i}`,
          points,
          tone: style.tone,
          width: style.width,
          ...('dashed' in style && style.dashed ? { dashed: true } : {}),
          ...('dotted' in style && style.dotted ? { dotted: true } : {}),
          lo: range.lo,
          hi: range.hi,
          marks:
            c.key === 'weight' && end !== undefined
              ? [{ at: 'end' as const, text: `${n(end, 1)} g`, strong: true }]
              : [],
        };
      }),
      rules: [],
      band: null,
      marks: [],
      bars: null,
    });
  }
  return {
    x: { from: 0, to, ticks: secondTicks(to) },
    lanes,
    span: null,
    gutter: 46,
    label: ctx.tr('compareChartLabel', { v: shots.map((x) => x.label).join(', ') }),
    whole: null,
    at: null,
    step: 0.5,
  };
}
